/* eslint-disable deprecate/member-expression */
import { type ButtonProps, Modal } from "antd";
import { type ModalFuncProps } from "antd/lib/modal";
import { Component, type FunctionComponent, type ReactNode } from "react";

import { cn } from "@btnet/utilities-core";
import { mobile, registerBackHandler, useMobileBackHandler } from "@btnet/utilities-webview";

import { AppProvider } from "helpers/AppProvider";
import { defaultAppProviderHandler } from "helpers/AppProvider.api.handler";

import { dispatchTracking, useTracking } from "utilities/analytics/analytics";
import { formattedPlural, type IFormattedPluralProps } from "utilities/string/string";

import {
    BTIconCheckCircleSuccess,
    BTIconCloseCircleError,
    BTIconInfoCircleFilled,
    BTIconWarningWarning,
} from "commonComponents/btWrappers/BTIcon";
import { BTModal, type IBTModalProps } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";

import "./BTConfirm.less";

/**
 * To disable the ok button, add disabled prop AND aria-disabled to the classNames to okButtonProps
 */
interface IBTModalFuncProps extends Omit<ModalFuncProps, "okButtonProps | cancelButtonProps"> {
    okButtonProps?: ButtonProps & { "data-testid"?: string };
    cancelButtonProps?: ButtonProps & { "data-testid"?: string };
}
const defaultModalProps: IBTModalFuncProps = {
    zIndex: 5000,
    centered: true,
    autoFocusButton: "cancel",
    icon: <></>,
    okButtonProps: {
        "data-testid": "confirmPrompt",
    } as any,
    cancelButtonProps: {
        "data-testid": "cancelPrompt",
    },
};

/**
 * Returns identifier that should be used in analytics tracking.
 * NOTE: We have limited info around these prompts, so this attempts to use what
 * relevant data we have in order to supply some context around the event.
 * @param props
 * @returns
 */
const getPromptTitleForTracking = (props: IBTModalFuncProps) => {
    if (typeof props.title === "string") {
        return props.title;
    } else if (typeof props.content === "string") {
        return props.content;
    } else {
        return "Confirmation";
    }
};

/**
 * Handles onOk and OnCancel callbacks and closing of the prompt if necessary based on antd
 * documentation here (see onOk & onCancel): https://ant.design/components/modal
 * @param callback onOk or onCancel callback supplied by the consumer
 * @param close close param supplied by antd
 */
const handleOkCancelCallback = async (
    callback: ((...args: any[]) => any) | undefined,
    close: any,
) => {
    // AntD allows the callback functions to control whether the prompt is closed
    // automatically, so mimicking that behavior via shouldNotClose.
    const shouldNotClose = await callback?.(close);

    // Manually close the prompt here if the supplied callback doesn't handle it (i.e. doesn't accept the param)
    // and didn't specify to NOT close
    if (shouldNotClose !== true && !callback?.length && typeof close === "function") {
        close();
    } else {
        return shouldNotClose;
    }
};

/**
 * Opens an imperative prompt and, in the mobile WebView, claims the native back button for it.
 *
 * antd renders these prompts through its own detached React root, so nothing in the app's component
 * tree spans the prompt's open→close to host `useMobileBackHandler` — the title and body wrappers
 * are React, but they render inside that separate root and don't own the prompt's lifecycle.
 * Without this, a back press falls through to whatever registered beneath the prompt — which in a
 * WebView pops the whole page and strands the prompt on screen. `destroy({ triggerCancel: true })`
 * is the same `close` antd wires to Escape and the mask, so back dismisses the prompt exactly as
 * those do, `onCancel` and all.
 * @param openPrompt Opens the prompt (an antd Modal static) and returns its instance
 * @param combinedProps Fully-built props, including the tracking `afterClose` each variant assigns
 */
const showImperativePrompt = function (
    // Only the `triggerCancel` overload is used here, so it's named rather than taking antd's
    // `(...args: any[])`.
    openPrompt: (props: IBTModalFuncProps) => {
        destroy: (triggerCancel: { triggerCancel: true }) => void;
    },
    combinedProps: IBTModalFuncProps,
) {
    if (!mobile.isWebview) {
        openPrompt(combinedProps);
        return;
    }

    let dismissed = false;

    // Holds the unregister returned below. The afterClose hook has to be handed to openPrompt, but
    // the registration needs the instance that same call returns — so the two are linked through
    // this holder rather than a closed-over value.
    const backHandler: { unregister?: () => void } = {};

    // Hook the afterClose the variant already assigned rather than reassigning it: antd fires
    // afterClose for OK, Cancel, mask, Escape, destroy and destroyAll, so the registration cannot
    // strand and leave back claimed app-wide after the prompt is gone.
    const instance = openPrompt({
        ...combinedProps,
        afterClose: () => {
            backHandler.unregister?.();
            combinedProps.afterClose?.();
        },
    });

    backHandler.unregister = registerBackHandler(() => {
        // Stay registered through the close animation. Releasing here would empty the registry
        // stack, which signals native that its default back behavior is live again — and the
        // animation is ~300ms, well inside double-tap range, so the second press would pop the
        // whole WebView. Keeping the claim lets the registry swallow that press instead;
        // afterClose above is the single release point.
        if (dismissed) {
            return;
        }
        dismissed = true;
        instance.destroy({ triggerCancel: true });
    });
};

/** Wraps antd Modal.confirm
 * NOTE: If onOk isn't wrapped in a function, additional props are passed.
 * @example
    btConfirm({
        title: "Are you sure you want to do this?",
        content: "Doing this may have unintended consequences",
        okText: "Yes",
        cancelText: "No",
        okType: "success",
        onOk: async () => {
            try {
                this.props.handler.doSomething(this.props.XXXXXXXXXXXXXXXXXX.id);
            }
            catch (e) {
                showAPIErrorMessage(e);
            }
        }
    });
 */
export const btConfirm = function (props: IBTModalFuncProps) {
    const promptTitle = getPromptTitleForTracking(props);
    const trackingData = {
        component: "Confirmation",
        uniqueId: promptTitle,
        extraInfo: {
            promptType: "btConfirm",
            promptTitle: promptTitle,
        },
    };

    props.title = wrapElementInAppProvider(props.title);
    props.content = wrapBodyContent(props.content);
    const combinedProps = { ...defaultModalProps, ...props };
    combinedProps.className = cn(combinedProps.className, "BTConfirm");
    combinedProps.modalRender = (node) => {
        return wrapModalRenderInFocusProvider(node, props.modalRender);
    };
    combinedProps.onCancel = async (close) => {
        dispatchTracking({
            ...trackingData,
            event: "ButtonClick",
            uniqueId: combinedProps.cancelButtonProps?.["data-testid"],
            element: "Button",
        });

        return await handleOkCancelCallback(props.onCancel, close);
    };
    combinedProps.onOk = async (close) => {
        dispatchTracking({
            ...trackingData,
            event: "ButtonClick",
            uniqueId: combinedProps.okButtonProps?.["data-testid"],
            element: "Button",
        });

        return await handleOkCancelCallback(props.onOk, close);
    };
    combinedProps.afterClose = () => {
        dispatchTracking({
            ...trackingData,
            event: "ModalClose",
            uniqueId: promptTitle,
            element: "Confirmation",
        });
        props.afterClose?.();
    };

    dispatchTracking({
        ...trackingData,
        event: "ModalOpen",
        uniqueId: promptTitle,
        element: "Confirmation",
    });
    showImperativePrompt((p) => Modal.confirm(p), combinedProps);
};

// Ignoring rule as we need to use a type with a literal in an intersection.
type IEntityDeleteProps = Omit<IBTModalFuncProps, "okText" | "title" | "content"> & {
    entityName: string | IFormattedPluralProps<string>;
};

interface IEntityMoveToTrashProps extends IEntityDeleteProps {
    additionalContent?: string;
}

type BTDeleteConfirmProps = IBTModalFuncProps | IEntityDeleteProps;

function isEntityDelete(
    props: IBTModalFuncProps | IEntityDeleteProps,
): props is IEntityDeleteProps {
    return (props as IEntityDeleteProps).entityName !== undefined;
}

const btDeleteConfirmBase = function (props: BTDeleteConfirmProps) {
    let entityNameToDisplay;
    if (isEntityDelete(props)) {
        entityNameToDisplay =
            typeof props.entityName !== "string"
                ? formattedPlural(props.entityName)
                : props.entityName;
    }
    const promptTitle = entityNameToDisplay
        ? `Delete ${entityNameToDisplay}`
        : getPromptTitleForTracking(props);
    const trackingData = {
        component: "Confirmation",
        uniqueId: promptTitle,
        extraInfo: {
            promptType: "btDeleteConfirm",
            promptTitle: promptTitle,
        },
    };

    const deleteDefaultProps: IBTModalFuncProps = {
        autoFocusButton: "cancel",
        okType: "danger",
        okText: "Delete",
    };

    if (isEntityDelete(props)) {
        deleteDefaultProps.okText = `Delete`;
        deleteDefaultProps.okType = "danger";
        deleteDefaultProps.title = `Delete ${entityNameToDisplay}?`;
        deleteDefaultProps.content = `Are you sure you want to permanently delete ${
            typeof props.entityName !== "string"
                ? props.entityName.value > 1
                    ? "these"
                    : "this"
                : "this"
        } ${entityNameToDisplay}?`;
    }

    const deleteCombinedProps = { ...defaultModalProps, ...deleteDefaultProps };
    const combinedProps = { ...deleteCombinedProps, ...props };

    combinedProps.title = wrapElementInAppProvider(combinedProps.title);
    combinedProps.content = wrapBodyContent(combinedProps.content);
    combinedProps.className = cn(combinedProps.className, "BTConfirm");
    combinedProps.modalRender = (node) => {
        return wrapModalRenderInFocusProvider(node, props.modalRender);
    };

    combinedProps.onCancel = async (close) => {
        dispatchTracking({
            ...trackingData,
            event: "ButtonClick",
            uniqueId: combinedProps.cancelButtonProps?.["data-testid"],
            element: "Button",
        });

        return await handleOkCancelCallback(props.onCancel, close);
    };
    combinedProps.onOk = async (close) => {
        dispatchTracking({
            ...trackingData,
            event: "ButtonClick",
            uniqueId: combinedProps.okButtonProps?.["data-testid"],
            element: "Button",
        });

        return await handleOkCancelCallback(props.onOk, close);
    };
    combinedProps.afterClose = () => {
        dispatchTracking({
            ...trackingData,
            event: "ModalClose",
            uniqueId: promptTitle,
            element: "Confirmation",
        });
        props.afterClose?.();
    };

    dispatchTracking({
        ...trackingData,
        event: "ModalOpen",
        uniqueId: promptTitle,
        element: "Confirmation",
    });
    showImperativePrompt((p) => Modal.confirm(p), combinedProps);
};

/**
 *
 * @param itemName
 * @param props
 * @example
 * btDeleteConfirm({
 *   entityName: "Change Order",
 *   onOk: async () => {
 *      // delete api call here...
 *   }
 * })
 */
export const btDeleteConfirm = function (props: BTDeleteConfirmProps) {
    btDeleteConfirmBase(props);
};

export const btMoveToTrashConfirm = function (props: IEntityMoveToTrashProps) {
    const entityNameToDisplay =
        typeof props.entityName !== "string" ? formattedPlural(props.entityName) : props.entityName;
    let content = `Are you sure you want to move ${
        typeof props.entityName !== "string"
            ? formattedPlural({ value: props.entityName.value, one: "this", other: "these" })
            : "this"
    } ${entityNameToDisplay} to trash?`;

    if (props.additionalContent) {
        content = `${content} ${props.additionalContent}`;
    }

    const moveToTrashProps = {
        ...props,
        okText: `Move to trash`,
        title: "Move to trash?",
        content,
    };
    btDeleteConfirmBase(moveToTrashProps);
};

export const btDeleteForeverConfirm = function (props: IEntityDeleteProps) {
    const entityNameToDisplay =
        typeof props.entityName !== "string" ? formattedPlural(props.entityName) : props.entityName;
    const content = `${
        typeof props.entityName !== "string"
            ? formattedPlural({ value: props.entityName.value, one: "This", other: "These" })
            : "This"
    } ${entityNameToDisplay} will be deleted forever and cannot be recovered.`;

    const moveToTrashProps = {
        ...props,
        okText: `Delete forever`,
        title: "Delete forever?",
        content,
    };
    btDeleteConfirmBase(moveToTrashProps);
};

/** Wraps antd Modal.info */
export const btInfo = function (props: IBTModalFuncProps) {
    const promptTitle = getPromptTitleForTracking(props);
    const trackingData = {
        component: "Confirmation",
        uniqueId: promptTitle,
        extraInfo: {
            promptType: "btInfo",
            promptTitle: promptTitle,
        },
    };

    // Per UX, we still want to show the icon on this modal. bt-109213.
    const { ...rest } = defaultModalProps;
    props.title = wrapElementInAppProvider(props.title);
    props.content = wrapBodyContent(props.content);
    const combinedProps = { ...rest, ...props };
    combinedProps.className = cn(combinedProps.className, "BTConfirm");
    combinedProps.modalRender = (node) => {
        return wrapModalRenderInFocusProvider(node, props.modalRender);
    };

    combinedProps.onCancel = async (close) => {
        dispatchTracking({
            ...trackingData,
            event: "ButtonClick",
            uniqueId: combinedProps.cancelButtonProps?.["data-testid"],
            element: "Button",
        });

        return await handleOkCancelCallback(props.onCancel, close);
    };
    combinedProps.onOk = async (close) => {
        dispatchTracking({
            ...trackingData,
            event: "ButtonClick",
            uniqueId: combinedProps.okButtonProps?.["data-testid"],
            element: "Button",
        });

        return await handleOkCancelCallback(props.onOk, close);
    };
    combinedProps.afterClose = () => {
        dispatchTracking({
            ...trackingData,
            event: "ModalClose",
            uniqueId: promptTitle,
            element: "Confirmation",
        });
        props.afterClose?.();
    };

    dispatchTracking({
        ...trackingData,
        event: "ModalOpen",
        uniqueId: promptTitle,
        element: "Confirmation",
    });
    showImperativePrompt((p) => Modal.info(p), combinedProps);
};

/** Wraps antd Modal.error */
export const btError = function (props: IBTModalFuncProps) {
    const promptTitle = getPromptTitleForTracking(props);
    const trackingData = {
        component: "Confirmation",
        uniqueId: promptTitle,
        extraInfo: {
            promptType: "btError",
            promptTitle: promptTitle,
        },
    };

    // Per UX, we still want to show the icon on this modal. bt-109213.
    const { ...rest } = defaultModalProps;
    props.title = wrapElementInAppProvider(props.title);
    props.content = wrapBodyContent(props.content);
    const combinedProps = { ...rest, ...props };
    combinedProps.className = cn(combinedProps.className, "BTConfirm");
    combinedProps.modalRender = (node) => {
        return wrapModalRenderInFocusProvider(node, props.modalRender);
    };

    combinedProps.onCancel = async (close) => {
        dispatchTracking({
            ...trackingData,
            event: "ButtonClick",
            uniqueId: combinedProps.cancelButtonProps?.["data-testid"],
            element: "Button",
        });

        return await handleOkCancelCallback(props.onCancel, close);
    };
    combinedProps.onOk = async (close) => {
        dispatchTracking({
            ...trackingData,
            event: "ButtonClick",
            uniqueId: combinedProps.okButtonProps?.["data-testid"],
            element: "Button",
        });

        return await handleOkCancelCallback(props.onOk, close);
    };
    combinedProps.afterClose = () => {
        dispatchTracking({
            ...trackingData,
            event: "ModalClose",
            uniqueId: promptTitle,
            element: "Confirmation",
        });
        props.afterClose?.();
    };

    dispatchTracking({
        ...trackingData,
        event: "ModalOpen",
        uniqueId: promptTitle,
        element: "Confirmation",
    });
    showImperativePrompt((p) => Modal.error(p), combinedProps);
};

/** Wraps antd Modal.warning */
export const btWarning = function (props: IBTModalFuncProps) {
    const promptTitle = getPromptTitleForTracking(props);
    const trackingData = {
        component: "Confirmation",
        uniqueId: promptTitle,
        extraInfo: {
            promptType: "btWarning",
            promptTitle: promptTitle,
        },
    };

    props.title = wrapElementInAppProvider(props.title);
    props.content = wrapBodyContent(props.content);
    const combinedProps = { ...defaultModalProps, ...props };
    combinedProps.className = cn(combinedProps.className, "BTConfirm");
    combinedProps.modalRender = (node) => {
        return wrapModalRenderInFocusProvider(node, props.modalRender);
    };

    combinedProps.onCancel = async (close) => {
        dispatchTracking({
            ...trackingData,
            event: "ButtonClick",
            uniqueId: combinedProps.cancelButtonProps?.["data-testid"],
            element: "Button",
        });

        return await handleOkCancelCallback(props.onCancel, close);
    };
    combinedProps.onOk = async (close) => {
        dispatchTracking({
            ...trackingData,
            event: "ButtonClick",
            uniqueId: combinedProps.okButtonProps?.["data-testid"],
            element: "Button",
        });

        return await handleOkCancelCallback(props.onOk, close);
    };
    combinedProps.afterClose = () => {
        dispatchTracking({
            ...trackingData,
            event: "ModalClose",
            uniqueId: promptTitle,
            element: "Confirmation",
        });
        props.afterClose?.();
    };

    dispatchTracking({
        ...trackingData,
        event: "ModalOpen",
        uniqueId: promptTitle,
        element: "Confirmation",
    });
    showImperativePrompt((p) => Modal.warning(p), combinedProps);
};

// Wrapping content in app provider for when we alert currencies
const wrapElementInAppProvider = function (element?: React.ReactNode) {
    if (element) {
        return (
            <AppProvider isRoot={false} handler={defaultAppProviderHandler}>
                {element}
            </AppProvider>
        );
    }
    return undefined;
};

// Wrapping modal body content in app provider and in a container div to set modal extents
const wrapBodyContent = function (bodyContent?: React.ReactNode) {
    if (bodyContent) {
        return (
            <div className="BTConfirm-BodyContainer">{wrapElementInAppProvider(bodyContent)}</div>
        );
    }
    return undefined;
};

const wrapModalRenderInFocusProvider = function (
    node: ReactNode,
    modalRender?: (node: ReactNode) => ReactNode,
) {
    return <FocusProvider>{modalRender ? modalRender(node) : node}</FocusProvider>;
};

export type ModalReturnTypes = "ok" | "cancel";

const promisify = (modalFunc: (props: IBTModalFuncProps) => void) => {
    const asyncModalFunc = async (props: Omit<IBTModalFuncProps, "onOk" | "onCancel">) => {
        return new Promise<ModalReturnTypes>((resolve) => {
            modalFunc({
                ...props,
                onOk: () => resolve("ok"),
                onCancel: () => resolve("cancel"),
            });
        });
    };

    return asyncModalFunc;
};

export const btConfirmAsync = promisify(btConfirm);
export const btInfoAsync = promisify(btInfo);
export const btWarningAsync = promisify(btWarning);
export const btErrorAsync = promisify(btError);

// The following logic is necessary because of issues w/ Omit in conjunction with union types
// (can't do Omit<IBTModalFuncProps | IEntityDeleteProps, "onOk" | "onCancel"> in promisify)
export const btDeleteConfirmAsync = async (props: BTDeleteConfirmProps) => {
    if (props.onOk || props.onCancel) {
        throw "onOk / onCancel are not valid props. Instead, use the promise result to determine control flow";
    }

    return new Promise<ModalReturnTypes>((resolve) => {
        btDeleteConfirm({
            ...props,
            onOk: () => resolve("ok"),
            onCancel: () => resolve("cancel"),
        });
    });
};

type ExcludeFromWrapper = "centered" | "width" | "closable" | "beforeClose" | "setPageTitle";
export type BTConfirmIconType = "info" | "warning" | "error" | "success";
export interface IBTConfirmProps extends Omit<IBTModalProps<unknown>, ExcludeFromWrapper> {
    icon?: BTConfirmIconType;
    fitToContent?: boolean;
    closable?: boolean;
    beforeClose?: () => void;
    /**
     * Removes the modal's header. This will also make the modal NOT closeable and also NOT show the modal's title.
     * @default false
     */
    removeHeader?: boolean;
}

/**
 * Stand-in for the `beforeClose` BTModal requires but a BTConfirm consumer may omit. Module-level so
 * the prop identity stays stable across renders.
 */
const noop = (): void => {};

/**
 * Temporary wrapper to disguise a BTModal as a confirm.
 */
export function BTConfirm({
    icon,
    title,
    children,
    className,
    removeHeader = false,
    closable = false,
    beforeClose,
    fitToContent,
    visible,
    afterClose = noop,
    footer = null,
    removeBodyPadding = false,
    isLoading = false,
    overflow = "auto",
    ...otherProps
}: IBTConfirmProps): ReturnType<FunctionComponent<IBTConfirmProps>> {
    // Route native back to the consumer's close callback while this confirm is open. A default
    // BTConfirm is otherwise sealed in the WebView — closable defaults to false, so there's no X,
    // BTModal hardcodes maskClosable false, and removeHeader turns Escape off — and without a
    // handler the press falls through to native, popping the page and stranding the confirm.
    // Gated on beforeClose existing: claiming back for a confirm with no close path would swallow
    // the press into a no-op and leave the button dead. Safe to call unconditionally — the hook
    // no-ops outside the WebView. Not wrapped in useCallback: the hook holds the handler in a ref,
    // so a fresh closure each render never churns the LIFO stack.
    useMobileBackHandler(() => beforeClose?.(), visible && beforeClose !== undefined);

    const { Track } = useTracking({
        component: "Confirmation",
        uniqueId: title,
        extraInfo: "BTConfirm",
    });

    let iconNode: ReactNode;
    switch (icon) {
        case "error":
            iconNode = <BTIconCloseCircleError />;
            break;

        case "info":
            iconNode = <BTIconInfoCircleFilled type="info" />;
            break;

        case "success":
            iconNode = <BTIconCheckCircleSuccess />;
            break;

        case "warning":
            iconNode = <BTIconWarningWarning />;
            break;
    }

    return (
        <Track>
            <BTModal
                data-testid="btModalConfirm"
                {...otherProps}
                visible={visible}
                afterClose={afterClose}
                footer={footer}
                removeBodyPadding={removeBodyPadding}
                isLoading={isLoading}
                overflow={overflow}
                setPageTitle={false}
                centered
                className={cn("BTConfirm", className)}
                width={fitToContent ? "auto" : "416px"}
                closable={closable && !!beforeClose}
                beforeClose={beforeClose ?? noop}
                removeHeader={removeHeader || !closable || !beforeClose}
            >
                {title && (
                    <div className="BTConfirmTitle">
                        {iconNode}
                        <BTTitle level={3}>{title}</BTTitle>
                    </div>
                )}
                {children}
            </BTModal>
        </Track>
    );
}

export class BTConfirmHeader extends Component<{ className?: string; children?: ReactNode }> {
    render = () => divWithClass("BTConfirmHeader", this.props.className, this.props.children);
}

export class BTConfirmContent extends Component<{ className?: string; children?: ReactNode }> {
    render = () => divWithClass("BTConfirmContent", this.props.className, this.props.children);
}

export class BTConfirmFooter extends Component<{ className?: string; children?: ReactNode }> {
    render = () => divWithClass("BTConfirmFooter", this.props.className, this.props.children);
}

function divWithClass(mainClassName: string, otherClassName?: string, children?: ReactNode) {
    return <div className={cn(mainClassName, otherClassName)}>{children}</div>;
}
